import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, User, UserPlus,
  HelpCircle, ChevronDown, Plus, RefreshCw, X, Check, Search,
  Trash2, Bell, FileText, AlertCircle, Mail, MessageSquare, Tv, Volume2,
  CheckCircle, Clock, XCircle, UserCheck, Stethoscope, PlayCircle
} from 'lucide-react';
import AppointmentModal from '../components/AppointmentModal';
import StatusDropdown from '../components/StatusDropdown';
import PatientBusyAlert from '../components/PatientBusyAlert';
import FreeSlotFinder from '../components/FreeSlotFinder';
import Modal from '../components/Modal';
import Spinner from '../components/Spinner';
import { useNotifications } from '../context/NotificationContext';
import { useCalendar, VIEW_MODES, getLocalISODate, isSlotValidForProf } from '../hooks/useCalendar';
import { supabase } from '../supabaseClient';
import './Calendar.css';

const getProfessionalTheme = (name) => {
  const themes = {
    'VISTA DIARIA': { color: '#0284c7', initials: 'VD' },
    'VISTA SEMANAL': { color: '#0284c7', initials: 'VS' },
    'VISTA MENSUAL': { color: '#0284c7', initials: 'VM' },
    'VER TODOS': { color: '#42a5f5', initials: 'T' },
    'VER LOS QUE ATIENDEN x DIA': { color: '#ec407a', initials: 'A' },
    'VER x SELECCIÓN': { color: '#ab47bc', initials: 'S' },
    'CONSULTORIOS EXTERNOS': { color: '#7e57c2', initials: 'CE' },
    'ECOGRAFIAS .': { color: '#ffee58', initials: 'E' },
    'FONOAUDIOLOGA .': { color: '#ffa726', initials: 'F' },
    'NEUMONOLOGO .': { color: '#fdd835', initials: 'N' },
    'NEUROLOGO .': { color: '#d4e157', initials: 'N' },
    'OTORRINO .': { color: '#66bb6a', initials: 'O' },
    'PSICOLOGIA .': { color: '#d4e157', initials: 'P' },
    'RAYOS X': { color: '#26c6da', initials: 'RX' },
    'AGREGAR OTRO PROFESIONAL': { color: '#26a69a', initials: 'A' }
  };
  return themes[name] || { color: '#0284c7', initials: name?.substring(0, 2)?.toUpperCase() || '?' };
};

const renderAppStatusIcon = (status) => {
  const s = status?.toLowerCase();
  switch (s) {
    case 'presente':
      return <span className="app-status-icon-badge presente" title="PRESENTE"><UserCheck size={12} /></span>;
    case 'confirmado':
      return <span className="app-status-icon-badge confirmado" title="CONFIRMADO"><CheckCircle size={12} /></span>;
    case 'pendiente':
      return <span className="app-status-icon-badge pendiente" title="PENDIENTE"><Clock size={12} /></span>;
    case 'en-consultorio':
    case 'en_consultorio':
      return <span className="app-status-icon-badge en-consultorio" title="EN CONSULTORIO"><Stethoscope size={12} /></span>;
    case 'en-llamado':
    case 'en_llamado':
      return <span className="app-status-icon-badge en-llamado" title="EN LLAMADO"><PlayCircle size={12} /></span>;
    case 'atendido':
    case 'completado':
      return <span className="app-status-icon-badge atendido" title="ATENDIDO / COMPLETADO"><Check size={12} /></span>;
    case 'cancelado':
    case 'ausente':
      return <span className="app-status-icon-badge cancelado" title="CANCELADO / AUSENTE"><XCircle size={12} /></span>;
    default:
      return null;
  }
};

const Dropdown = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const currentTheme = getProfessionalTheme(value);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(o => !o);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div 
      className="custom-dropdown" 
      ref={ref} 
      onClick={() => setOpen(o => !o)}
      onKeyDown={handleKeyDown}
      role="listbox"
      aria-expanded={open}
      tabIndex="0"
    >
      <div className="dropdown-trigger-content">
        <div className="option-circle" style={{ backgroundColor: currentTheme.color }}>
          {currentTheme.initials}
        </div>
        <span>{value}</span>
      </div>
      <ChevronDown size={16} className={`dropdown-arrow ${open ? 'open' : ''}`} />
      {open && (
        <div className="dropdown-menu">
          {options.map(opt => {
            const theme = getProfessionalTheme(opt);
            return (
              <div
                key={opt}
                className={`dropdown-item ${opt === value ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); onChange(opt); setOpen(false); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation(); onChange(opt); setOpen(false);
                  }
                }}
                role="option"
                aria-selected={opt === value}
                tabIndex="0"
              >
                <div className="option-circle" style={{ backgroundColor: theme.color }}>
                  {theme.initials}
                </div>
                <span>{opt}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const formatHeaderDate = (date) =>
  date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const Calendar = () => {
  const { addNotification } = useNotifications();
  const [isInboxOpen, setIsInboxOpen] = React.useState(false);
  const [inboxApps, setInboxApps] = React.useState([]);
  const [inboxLoading, setInboxLoading] = React.useState(false);

  const fetchInbox = async () => {
    setInboxLoading(true);
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .eq('appointment_time', '00:00:00')
        .neq('status', 'cancelado')
        .neq('status', 'atendido')
        .order('created_at', { ascending: false });
      if (data) setInboxApps(data);
    } catch(e) { addNotification({ title: 'Error', message: 'Error al cargar la bandeja de turnos', type: 'error' }); }
    setInboxLoading(false);
  };

  React.useEffect(() => {
    fetchInbox();
  }, []);

  const {
    isAdmin, currentDate, setCurrentDate, selectedOffice, setSelectedOffice,
    viewMode, setViewMode, isModalOpen, setIsModalOpen, modalData, setModalData,
    appointments, loading, emailModalData, setEmailModalData, emailInput, setEmailInput,
    showScheduleModal, setShowScheduleModal, showInfoModal, setShowInfoModal,
    localSearchQuery, setLocalSearchQuery, selectedPortalApp, setSelectedPortalApp,
    isFinderOpen, setIsFinderOpen, expandedAppId, timeSlots, toggleExpandApp,
    handlePrevDay, handleNextDay, goToToday, openNewAppointment, handleSelectSlotFromFinder,
    openNewAppointmentWeekly, getWeekDays, getMonthDays, saveAppointment, handleCambiarHorarios,
    handleInfoProf, handleCreatePatientFromPortal, handleResendPortalEmail, handleSendEmail,
    deleteAppointment, updateAppointmentStatus, updateAppointmentNotes, updateAppointmentDetails, fetchAppointments, professionals, holidays, clipboardApp, setClipboardApp
  } = useCalendar();

  const [optionsModalApp, setOptionsModalApp] = React.useState(null);
  const [editingNotesId, setEditingNotesId] = React.useState(null);
  const [editNotesText, setEditNotesText] = React.useState('');
  const [isEditingAppDetails, setIsEditingAppDetails] = React.useState(false);
  const [editAppDetails, setEditAppDetails] = React.useState({});

  const weeklyAppCounts = React.useMemo(() => {
    if (viewMode !== 'VISTA SEMANAL') return {};
    const counts = {};
    appointments.forEach(a => {
      if (a.appointment_date && a.appointment_time) {
        const key = `${a.appointment_date}_${a.appointment_time.substring(0, 2)}`;
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [appointments, viewMode]);

    const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const todayStr = dayNames[currentDate.getDay()];
  
  const multiColumnOffices = professionals.filter(p => {
    if (p.is_hidden || p.type !== 'Profesional') return false;
    if (selectedOffice === 'VER LOS QUE ATIENDEN x DIA') {
      const localDateStr = getLocalISODate(currentDate);
      if (p.working_hours?.specific_dates?.[localDateStr]) return true;
      return p.working_hours?.[todayStr]?.enabled === true;
    }
    return true; // For VER TODOS and VER x SELECCION (defaulting to all)
  });

  return (
    <div className="calendar-page">
      <div className="calendar-top-bar v2 compact two-rows">
        {/* Row 1: Selection & Search */}
        <div className="bar-row toolbar-row-top">
          <div className="view-controls">
            {isAdmin ? (
              <Dropdown value={selectedOffice} options={['VER TODOS', 'VER LOS QUE ATIENDEN x DIA', 'VER x SELECCIÓN', ...professionals.map(p => p.full_name), 'AGREGAR OTRO PROFESIONAL']} onChange={setSelectedOffice} />
            ) : (
              <div className="locked-office-badge">
                {selectedOffice}
              </div>
            )}
            <Dropdown value={viewMode} options={VIEW_MODES} onChange={(v) => setViewMode(v)} />
          </div>

          <div className="omnibar-container calendar-search">
            <div className="omnibar">
              <Search size={16} className="search-icon" />
              <input 
                type="text" 
                placeholder="Buscar por nombre, estudio o DNI..." 
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Row 2: Date Navigation & Action Buttons */}
        <div className="bar-row toolbar-row-bottom">
          <div className="date-nav">
            <button className="nav-btn" onClick={handlePrevDay} title="Día Anterior"><ChevronLeft size={16} /><span className="nav-btn-text"> ANTERIOR</span></button>
            <div className="date-picker-wrapper">
              <input 
                type="date"
                value={getLocalISODate(currentDate)}
                onChange={(e) => {
                  if (e.target.value) {
                    const [y, m, d] = e.target.value.split('-');
                    setCurrentDate(new Date(y, m - 1, d));
                  }
                }}
                id="hidden-date-picker"
              />
              <button 
                className="calendar-icon-btn" 
                title="Seleccionar Fecha"
                onClick={() => {
                  const picker = document.getElementById('hidden-date-picker');
                  if (picker && picker.showPicker) {
                    picker.showPicker();
                  } else if (picker) {
                    picker.focus();
                  }
                }}
              ><CalendarIcon size={16} /></button>
            </div>
            <button className="nav-btn hoy-btn" onClick={goToToday}>HOY</button>
            <button className="nav-btn" onClick={handleNextDay} title="Día Siguiente"><span className="nav-btn-text">SIG </span><ChevronRight size={16} /></button>
          </div>

          <div className="button-group">
            <button className="btn-secondary btn-inbox-badge" onClick={() => { fetchInbox(); setIsInboxOpen(true); }}>
              <Bell size={15} /> <span className="btn-text">POR ASIGNAR</span>
              {inboxApps.length > 0 && <span className="inbox-count-badge">{inboxApps.length}</span>}
            </button>
            <button className="btn-primary btn-turno-libre" onClick={() => setIsFinderOpen(true)}>
              <span className="btn-text">BUSCAR TURNO LIBRE</span>
            </button>
            <button className="btn-secondary" onClick={handleCambiarHorarios}>HORARIOS</button>
            <button className="btn-secondary" onClick={handleInfoProf}>INFO PROF.</button>
          </div>
        </div>

        {clipboardApp && (
          <div className="clipboard-notice-bar">
            <span>⚠️ El turno de {clipboardApp.patient_name || 'este paciente'} está en memoria. Hacé click en cualquier horario y día vacío para pegarlo.</span>
            <button onClick={() => setClipboardApp(null)}><X size={16} /></button>
          </div>
        )}
      </div>

      <div className="calendar-content">
        <h1 className="current-date-title">
          {viewMode === 'VISTA SEMANAL' 
            ? `Semana del ${getWeekDays()[0].toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}`
            : formatHeaderDate(currentDate)}
        </h1>

        <div className="calendar-grid-container card">
        {loading ? (
          <Spinner text="Cargando agenda..." />
        ) : (
          <div className={`calendar-grid ${viewMode === 'VISTA SEMANAL' ? 'weekly-view' : viewMode === 'VISTA MENSUAL' ? 'monthly-view' : 'daily-view'}`}>
                        {viewMode === 'VISTA DIARIA' && ['VER TODOS', 'VER LOS QUE ATIENDEN x DIA', 'VER x SELECCIÓN', 'AGREGAR OTRO PROFESIONAL'].includes(selectedOffice) && (
              <div className="daily-multi-container">
                <div className="multi-header-row">
                  <div className="multi-time-header">Hora</div>
                  {multiColumnOffices.map(prof => (
                    <div key={prof.id} className="multi-header-cell">
                      <div className="prof-badge-sm" style={{ backgroundColor: getProfessionalTheme(prof.full_name).color }}>
                        {getProfessionalTheme(prof.full_name).initials}
                      </div>
                      <span className="prof-name">{prof.full_name}</span>
                    </div>
                  ))}
                </div>
                <div className="multi-body">
                  {timeSlots.length === 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: 'var(--text-muted)', width: '100%' }}>
                      <CalendarIcon size={48} style={{ opacity: 0.2, marginBottom: '16px' }} />
                      <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>Sin horarios disponibles</h3>
                      <p style={{ margin: 0 }}>Ningún profesional atiende en este día.</p>
                    </div>
                  )}
                  {timeSlots.map((time, slotIdx) => {
                    const displayTime = time.substring(0, 5);
                    const nextTime = timeSlots[slotIdx + 1];
                    const nextDisplayTime = nextTime ? nextTime.substring(0, 5) : '23:59';
                    return (
                      <div key={time} className="multi-body-row">
                        <div className="multi-time-cell">{displayTime}</div>
                        {multiColumnOffices.map(prof => {
                          const slotApps = appointments.filter(a => {
                            if (!a.appointment_time || a.professional_name !== prof.full_name) return false;
                            const appTime = a.appointment_time.substring(0, 5);
                            return appTime === displayTime;
                          });
                          const isOccupied = slotApps.length > 0;
                          const maxOverbooking = prof.working_hours?.max_overbooking || 0;
                          const isFull = slotApps.length >= (1 + maxOverbooking);
                          const isWorking = isSlotValidForProf(prof, currentDate, displayTime, holidays);
                          
                          if (!isWorking && !isOccupied) {
                            return (
                              <div key={prof.id} className="multi-slot-cell blocked out-of-hours" title="Bloqueado (Fuera de horario)" style={{ opacity: 0.5, cursor: 'not-allowed', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span className="blocked-text" style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', userSelect: 'none', pointerEvents: 'none', fontWeight: '500' }}>
                                  BLOQUEADO
                                </span>
                              </div>
                            );
                          }

                          return (
                            <div key={prof.id} className={`multi-slot-cell ${isOccupied ? 'occupied' : 'free'} ${isFull ? 'full' : 'has-space'}`} onClick={() => !isFull && openNewAppointment(displayTime, prof.full_name)} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                              {slotApps.map(app => (
                                <div key={app.id} className={`compact-app-card ${app.status}`} onClick={(e) => { e.stopPropagation(); setOptionsModalApp(app); }}>
                                  <div className="compact-app-header-row">
                                    <div className="compact-app-time">{app.appointment_time?.substring(0, 5)} {app.study_details?.patient_dni ? '- DNI ' + app.study_details.patient_dni : ''}</div>
                                    {renderAppStatusIcon(app.status)}
                                  </div>
                                  <div className="compact-app-name">{app.patient_name || 'Sin nombre'}</div>
                                </div>
                              ))}
                              {isOccupied && maxOverbooking > 0 && !isFull && (
                                <div className="compact-overbook" onClick={(e) => { e.stopPropagation(); openNewAppointment(displayTime, prof.full_name); }} style={{ border: '1px dashed rgba(0,229,255,0.4)', borderRadius: '4px', padding: '2px', display: 'flex', justifyContent: 'center', color: '#00e5ff', cursor: 'pointer', marginTop: '2px' }}>
                                  <Plus size={12} />
                                </div>
                              )}
                              {!isOccupied && <div className="empty-slot"><Plus size={14} /></div>}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {viewMode === 'VISTA DIARIA' && !['VER TODOS', 'VER LOS QUE ATIENDEN x DIA', 'VER x SELECCIÓN', 'AGREGAR OTRO PROFESIONAL'].includes(selectedOffice) && timeSlots.length === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'var(--bg-glass)', borderRadius: '12px', border: '1px solid var(--border-glass)' }}>
                <CalendarIcon size={48} style={{ opacity: 0.2, marginBottom: '16px' }} />
                <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>Agenda no disponible</h3>
                <p style={{ margin: 0 }}>El profesional no atiende este día o se encuentra bloqueado por vacaciones/feriado.</p>
              </div>
            )}
            {viewMode === 'VISTA DIARIA' && !['VER TODOS', 'VER LOS QUE ATIENDEN x DIA', 'VER x SELECCIÓN', 'AGREGAR OTRO PROFESIONAL'].includes(selectedOffice) && timeSlots.map((time, slotIdx) => {
              const displayTime = time.substring(0, 5);
              const nextTime = timeSlots[slotIdx + 1];
              const nextDisplayTime = nextTime ? nextTime.substring(0, 5) : '23:59';
              const slotApps = appointments.filter(a => {
                if (!a.appointment_time) return false;
                if (selectedOffice && a.professional_name !== selectedOffice) return false;
                const appTime = a.appointment_time.substring(0, 5);
                return appTime === displayTime;
              });
              const isOccupied = slotApps.length > 0;
              const currentProf = professionals.find(p => p.full_name === selectedOffice);
              const isWorking = currentProf ? isSlotValidForProf(currentProf, currentDate, displayTime, holidays) : true;
              const maxOverbooking = currentProf?.working_hours?.max_overbooking || 0;
              const isFull = slotApps.length >= (1 + maxOverbooking);

              if (!isWorking && !isOccupied) {
                return (
                  <div key={time} className="grid-row blocked out-of-hours" title="Bloqueado (Fuera de horario)" style={{ background: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.12), rgba(0,0,0,0.12) 10px, rgba(255,255,255,0.015) 10px, rgba(255,255,255,0.015) 20px)', opacity: 0.5, cursor: 'not-allowed' }}>
                    <div className="time-cell" style={{ color: 'var(--text-muted)' }}>{displayTime}</div>
                    <div className="slot-cell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', letterSpacing: '1px', userSelect: 'none', pointerEvents: 'none', fontWeight: '500' }}>
                      BLOQUEADO
                    </div>
                  </div>
                );
              }

              return (
                <div key={time} className={`grid-row ${isOccupied ? 'occupied' : 'free'} ${isFull ? 'full' : 'has-space'}`} onClick={() => !isFull && openNewAppointment(displayTime)}>
                  <div className="time-cell">{displayTime}</div>
                  <div className="slot-cell" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
                    {slotApps.map(app => (
                        <div key={app.id} className={`appointment-card ${app.status} ${expandedAppId === app.id ? 'expanded' : ''}`} onClick={(e) => e.stopPropagation()}>
                          <div className="app-card-header">
                            <div className="app-main" onClick={() => toggleExpandApp(app.id)}>
                              <div className="app-avatar"><User size={14} /></div>
                              <div className="app-info">
                                <div className="app-patient-name">{app.patient_name || 'Sin nombre'}</div>
                                <div className="app-study">{app.study_type || 'Consulta'}</div>
                              </div>
                              <ChevronDown size={14} className={`app-expand-arrow ${expandedAppId === app.id ? 'rotated' : ''}`} />
                            </div>

                            <div className="app-actions-container">
                              <StatusDropdown value={app.status || 'pendiente'} onChange={(newStatus) => updateAppointmentStatus(app.id, newStatus)} />
                              <div className="reports-actions-group">
                                <button className="btn-report-action chat" onClick={(e) => { e.stopPropagation(); if(app.patient_email) { window.location.href = `mailto:${app.patient_email}?subject=Consulta Turno - GDT`; } else { alert('El paciente no tiene email configurado.'); } }} title="Enviar correo desde mi app" aria-label="Enviar correo" style={{ color: '#00e5ff', borderColor: 'rgba(0,229,255,0.3)', background: 'rgba(0,229,255,0.08)' }}><Mail size={14} /></button>
                                <button className="btn-report-action email" onClick={(e) => { e.stopPropagation(); setEmailModalData(app); }} title="Enviar Expediente" aria-label="Enviar Expediente"><FileText size={14} /></button>
                                <button className="btn-report-action" style={{ background: 'rgba(37, 211, 102, 0.1)', color: '#25D366', borderColor: 'rgba(37, 211, 102, 0.2)' }} onClick={() => window.open(`https://wa.me/${app.patient_phone?.replace(/\D/g, '')}`, '_blank')} title="WhatsApp" aria-label="WhatsApp"><MessageSquare size={14} /></button>
                                <button className="btn-report-action" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', borderColor: 'rgba(59, 130, 246, 0.2)' }} onClick={(e) => { e.stopPropagation(); updateAppointmentStatus(app.id, 'en-llamado'); }} title="Llamar al TV" aria-label="Llamar al TV"><Volume2 size={14} /></button>
                                <button className="btn-report-action docs" onClick={(e) => { 
                                  e.stopPropagation(); 
                                  if (app.patient_id) {
                                    navigate(`/patients/${app.patient_id}`); 
                                  } else {
                                    sessionStorage.setItem('pending_create_patient', JSON.stringify({
                                          full_name: app.patient_name || '',
                                          phone: app.patient_phone || '',
                                          email: app.patient_email || '',
                                          dni: app.study_details?.patient_dni || ''
                                    }));
                                    navigate('/patients');
                                  }
                                }} title={app.patient_id ? "Carpeta Médica" : "Crear Ficha de Paciente"} aria-label={app.patient_id ? "Carpeta Médica" : "Crear Ficha de Paciente"}>
                                  {app.patient_id ? <FileText size={14} /> : <UserPlus size={14} />}
                                </button>
                                <button className="btn-report-action delete" onClick={(e) => { e.stopPropagation(); deleteAppointment(app.id); }} title="Eliminar"><Trash2 size={14} /></button>
                              </div>
                            </div>
                          </div>

                          {expandedAppId === app.id && (
                            <div className="app-card-expanded-details">
                              <div className="expanded-details-grid">
                                <div className="detail-item">
                                  <span className="detail-label">Documento (DNI):</span>
                                  <span className="detail-value highlight">{app.study_details?.patient_dni || 'No especificado'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Teléfono:</span>
                                  <span className="detail-value">{app.patient_phone || 'No especificado'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Email:</span>
                                  <span className="detail-value">{app.patient_email || 'No especificado'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Fecha de Nac.:</span>
                                  <span className="detail-value">{app.study_details?.patient_birthdate || 'No especificada'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Obra Social / Prepaga:</span>
                                  <span className="detail-value">{app.study_details?.insurance_provider || 'PARTICULAR'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Plan Cobertura:</span>
                                  <span className="detail-value">{app.study_details?.insurance_plan || 'N/A'}</span>
                                </div>
                                <div className="detail-item full-width">
                                  <span className="detail-label">Profesional Asignado:</span>
                                  <span className="detail-value">{app.professional_name || 'No especificado'}</span>
                                </div>
                                {app.created_by_user && (
                                  <div className="detail-item full-width">
                                    <span className="detail-label">Creado por (Operador):</span>
                                    <span className="detail-value" style={{ color: 'var(--accent)', fontWeight: 'bold' }}>{app.created_by_user}</span>
                                  </div>
                                )}
                                {app.admin_notes && (
                                  <div className="detail-item full-width notes-item">
                                    <span className="detail-label">Notas Administrativas:</span>
                                    <p className="detail-notes">{app.admin_notes}</p>
                                  </div>
                                )}
                                {app.study_details?.attached_files?.length > 0 && (
                                  <div className="detail-item full-width files-item">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                      <span className="detail-label">Archivos Adjuntos ({app.study_details.attached_files.length}):</span>
                                      <label style={{ background: 'transparent', border: 'none', color: '#00e5ff', cursor: 'pointer', fontSize: '11px', padding: '0 2px', textDecoration: 'underline' }} onClick={(e) => e.stopPropagation()}>
                                        + Subir
                                        <input type="file" style={{ display: 'none' }} onChange={async (e) => {
                                          e.stopPropagation();
                                          const file = e.target.files[0];
                                          if (!file) return;
                                          try {
                                            addNotification({ title: 'Subiendo...', message: 'Subiendo archivo...', type: 'info' });
                                            const fileName = Date.now() + '_' + file.name.replace(/\s+/g, '_');
                                            const { error: uploadError } = await supabase.storage.from('patient-docs').upload(fileName, file);
                                            if (uploadError) throw uploadError;
                                            const { data: { publicUrl } } = supabase.storage.from('patient-docs').getPublicUrl(fileName);
                                            
                                            const newFile = { name: file.name, url: publicUrl, study_name: 'Adicional' };
                                            const currentFiles = app.study_details?.attached_files || [];
                                            const study_details = { ...app.study_details, attached_files: [...currentFiles, newFile] };
                                            
                                            await updateAppointmentDetails(app.id, { study_details });
                                            addNotification({ title: 'Éxito', message: 'Archivo subido', type: 'success' });
                                          } catch(err) {
                                            console.error(err);
                                            addNotification({ title: 'Error', message: 'No se pudo subir el archivo', type: 'error' });
                                          }
                                        }} />
                                      </label>
                                    </div>
                                    <div className="attached-files-list">
                                      {app.study_details.attached_files.map((file, idx) => (
                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', background: 'rgba(0,229,255,0.05)', padding: '2px 6px', borderRadius: '4px', border: '1px solid rgba(0,229,255,0.1)', marginBottom: '4px' }}>
                                          <a href={file.url} target="_blank" rel="noopener noreferrer" className="attached-file-link" onClick={(e) => e.stopPropagation()} style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            <FileText size={12} style={{ flexShrink: 0 }} />
                                            <span>{file.name}</span>
                                          </a>
                                          <button
                                            type="button"
                                            title="Eliminar este archivo adjunto"
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              if (!window.confirm('¿Está seguro de que desea eliminar este archivo adjunto?')) return;
                                              try {
                                                const currentFiles = app.study_details?.attached_files || [];
                                                const updatedFiles = currentFiles.filter((_, i) => i !== idx);
                                                const study_details = { ...app.study_details, attached_files: updatedFiles };
                                                await updateAppointmentDetails(app.id, { study_details });
                                                addNotification({ title: 'Éxito', message: 'Archivo eliminado', type: 'success' });
                                              } catch(err) {
                                                console.error(err);
                                                addNotification({ title: 'Error', message: 'No se pudo eliminar el archivo', type: 'error' });
                                              }
                                            }}
                                            style={{ background: 'transparent', border: 'none', color: '#ff5252', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                                          >
                                            <Trash2 size={13} />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    {isOccupied && maxOverbooking > 0 && !isFull && (
                      <div className="overbook-btn-container" style={{ display: 'flex', justifyContent: 'center', padding: '4px' }}>
                        <button 
                          className="btn-overbook" 
                          onClick={(e) => { e.stopPropagation(); openNewAppointment(displayTime); }}
                          style={{ background: 'rgba(0,229,255,0.1)', border: '1px dashed #00e5ff', color: '#00e5ff', width: '100%', padding: '6px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: '500' }}
                        >
                          <Plus size={14} /> Agregar sobre-turno
                        </button>
                      </div>
                    )}
                    {!isOccupied && (
                      <div className="empty-slot"><Plus size={14} /></div>
                    )}
                  </div>
                </div>
              );
            })}

            {viewMode === 'VISTA SEMANAL' && (
              <>
                {timeSlots.length === 0 && (
                  <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'var(--bg-glass)', borderRadius: '12px', border: '1px solid var(--border-glass)' }}>
                    <CalendarIcon size={48} style={{ opacity: 0.2, marginBottom: '16px' }} />
                    <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>Semana no disponible</h3>
                    <p style={{ margin: 0 }}>No hay horarios configurados en toda esta semana.</p>
                  </div>
                )}
                {timeSlots.length > 0 && (
                  <>
                    <div className="grid-header-cell">HORA</div>
                {getWeekDays().map(d => (
                  <div key={d.toString()} className="grid-header-cell">
                    {d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' })}
                  </div>
                ))}
                {timeSlots.filter(t => t.endsWith(':00:00')).map(time => (
                  <React.Fragment key={time}>
                    <div className="weekly-time-label">{time.substring(0, 5)}</div>
                    {getWeekDays().map(d => {
                      const dateStr = getLocalISODate(d);
                      const appCount = weeklyAppCounts[`${dateStr}_${time.substring(0, 2)}`] || 0;
                      
                      let isWorking = false;
                      let checkProfs = professionals;
                      if (selectedOffice && !['VER TODOS', 'VER LOS QUE ATIENDEN x DIA', 'VER x SELECCIÓN', 'AGREGAR OTRO PROFESIONAL'].includes(selectedOffice)) {
                        const p = professionals.find(x => x.full_name === selectedOffice);
                        if (p) checkProfs = [p];
                      }
                      for (const p of checkProfs) {
                        // Check if valid for ANY slot in this hour
                        if (isSlotValidForProf(p, d, time, holidays) || isSlotValidForProf(p, d, time.replace(':00:00', ':30:00'), holidays)) {
                          isWorking = true;
                          break;
                        }
                      }

                      if (!isWorking) {
                        return <div key={dateStr + time} className="slot-cell weekly blocked free" onClick={() => openNewAppointment(time.substring(0,5), selectedOffice === 'TODOS' || selectedOffice.startsWith('VER ') ? '' : selectedOffice)} style={{ background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.01), rgba(255,255,255,0.01) 10px, rgba(255,255,255,0.03) 10px, rgba(255,255,255,0.03) 20px)', cursor: 'pointer', position: 'relative' }}><div className="empty-slot"><Plus size={16} /></div></div>;
                      }

                      return (
                        <div key={dateStr + time} className="slot-cell weekly" onClick={() => openNewAppointmentWeekly(dateStr, time.substring(0, 5))}>
                          {appCount > 0 && <div className="app-dot-indicator">{appCount}</div>}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
                  </>
                )}
              </>
            )}

            {viewMode === 'VISTA MENSUAL' && (
              <>
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
                  <div key={d} className="grid-header-cell">{d}</div>
                ))}
                {getMonthDays().map((dObj, i) => {
                  const dateStr = getLocalISODate(dObj.date);
                  const dayApps = appointments.filter(a => a.appointment_date === dateStr);
                  const isToday = dateStr === getLocalISODate(new Date());

                  return (
                    <div 
                      key={i} 
                      className={`month-day-cell ${!dObj.isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
                      onClick={() => { setCurrentDate(dObj.date); setViewMode('VISTA DIARIA'); }}
                    >
                      <div className="day-number">{dObj.date.getDate()}</div>
                      {dayApps.length > 0 && (
                        <div className="day-appointments-count">
                          {dayApps.length} {dayApps.length === 1 ? 'Turno' : 'Turnos'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
        </div>
      </div>

      <AppointmentModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} initialData={modalData} onSave={saveAppointment} professionalsList={professionals} />
      
      <FreeSlotFinder 
        isOpen={isFinderOpen} 
        onClose={() => setIsFinderOpen(false)} 
        onSelectSlot={handleSelectSlotFromFinder}
        onGoToDate={(targetDate) => {
          setCurrentDate(targetDate);
          setViewMode('VISTA DIARIA');
          setIsFinderOpen(false);
        }}
        currentProfessional={selectedOffice}
        professionalsList={professionals}
      />

      <Modal 
        isOpen={!!selectedPortalApp} 
        onClose={() => setSelectedPortalApp(null)}
        title="EXPEDIENTE DIGITAL (PORTAL)"
        icon={FileText}
        iconColor="cyan"
        footer={
          <>
            <button className="btn-cancel-modal" onClick={() => setSelectedPortalApp(null)}>CERRAR</button>
            <button className="btn-action-secondary" onClick={() => handleResendPortalEmail(selectedPortalApp)}>RE-ENVIAR EMAIL</button>
            <button className="btn-save-appointment" onClick={() => handleCreatePatientFromPortal(selectedPortalApp)}>CREAR FICHA</button>
          </>
        }
      >
        <div className="portal-content-layout">
          <div className="info-main-section">
            <div className="section-label">Información Personal</div>
            <div className="data-card-premium">
              <div className="data-field">
                <label>Nombre Completo</label>
                <div className="data-value">{selectedPortalApp?.patient_name}</div>
              </div>
              <div className="data-field">
                <label>Documento (DNI)</label>
                <div className="data-value highlight">{selectedPortalApp?.study_details?.patient_dni || 'No especificado'}</div>
              </div>
              <div className="data-field">
                <label>Obra Social / Cobertura</label>
                <div className="data-value">{selectedPortalApp?.study_details?.insurance_provider || 'PARTICULAR'}</div>
              </div>
            </div>
          </div>

          <div className="info-files-section">
            <div className="section-label">Archivos Adjuntos ({selectedPortalApp?.study_details?.attached_files_count || 0})</div>
            <div className="files-container-premium">
              {selectedPortalApp?.study_details?.attached_files?.length > 0 ? (
                selectedPortalApp.study_details.attached_files.map((f, i) => (
                  <div key={i} className="file-item-premium">
                    <div className="file-icon orange"><FileText size={16} /></div>
                    <div className="file-name-wrapper">
                      <span className="file-name">{f.name}</span>
                    </div>
                    {f.url && <a href={f.url} target="_blank" rel="noreferrer" className="btn-view-file">VER</a>}
                  </div>
                ))
              ) : (
                <div className="empty-files-msg">
                  <AlertCircle size={20} />
                  <span>Sin archivos adjuntos.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!emailModalData}
        onClose={() => setEmailModalData(null)}
        title="Confirmar y Enviar"
        icon={Check}
        theme="dark"
        footer={
          <>
            <button className="btn-cancel-modal" onClick={() => setEmailModalData(null)}>CANCELAR</button>
            <button className="btn-save-appointment" onClick={handleSendEmail}>ENVIAR DATOS</button>
          </>
        }
      >
        <div className="modal-data-preview">
          <strong>Paciente:</strong> {emailModalData?.patient_name}<br/>
          <strong>Fecha:</strong> {emailModalData?.appointment_date}<br/>
          <strong>Hora:</strong> {emailModalData?.appointment_time?.substring(0,5)}
        </div>
        <div className="form-group full" style={{ marginTop: '1.5rem' }}>
          <label>CORREO DE LA SUCURSAL</label>
          <input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: 'white' }} />
        </div>
      </Modal>

      <Modal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        title={`GESTIÓN DE HORARIOS: ${selectedOffice}`}
        icon={CalendarIcon}
        iconColor="orange"
        footer={
          <>
            <button className="btn-cancel-modal" onClick={() => setShowScheduleModal(false)}>CERRAR</button>
            <button className="btn-save-appointment">GUARDAR CAMBIOS</button>
          </>
        }
      >
        <div className="schedule-grid-premium">
          {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'].map(day => (
            <div key={day} className="schedule-day-row">
              <div className="day-name">{day}</div>
              <div className="day-hours">08:00 - 13:00 | 14:00 - 20:00</div>
              <button className="btn-edit-hours">EDITAR</button>
            </div>
          ))}
        </div>
        <div className="info-alert-premium" style={{ marginTop: '1.5rem' }}>
          <AlertCircle size={16} />
          <span>Los cambios en los horarios afectarán la disponibilidad en el Portal del Paciente.</span>
        </div>
      </Modal>

      <Modal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        title="INFORMACIÓN DEL PROFESIONAL"
        icon={User}
        iconColor="cyan"
        footer={
          <>
            <button className="btn-cancel-modal" onClick={() => setShowInfoModal(false)}>CERRAR</button>
            <button className="btn-action-secondary">VER FICHA COMPLETA</button>
          </>
        }
      >
        <div className="prof-info-layout">
          <div className="prof-avatar-large" style={{ backgroundColor: getProfessionalTheme(selectedOffice).color }}>
            {getProfessionalTheme(selectedOffice).initials}
          </div>
          <div className="prof-details-content">
            <h2>{selectedOffice}</h2>
            <div className="prof-tag">Especialista Staff GDT</div>
            <div className="prof-bio">
              Profesional altamente calificado con más de 15 años de experiencia en la red de clínicas GDT. 
              Especializado en atención integral y gestión de pacientes de alta complejidad.
            </div>
            <div className="prof-stats-grid">
              <div className="stat-item">
                <div className="stat-label">Turnos / Mes</div>
                <div className="stat-val">342</div>
              </div>
              <div className="stat-item">
                <div className="stat-label">Calificación</div>
                <div className="stat-val">4.9/5.0</div>
              </div>
            </div>
          </div>
        </div>
      </Modal>


      <Modal isOpen={!!optionsModalApp} onClose={() => setOptionsModalApp(null)} title="Detalle del Turno">
        {optionsModalApp && (
          <div className="options-modal-content">
            <PatientBusyAlert appointment={optionsModalApp} />
            
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, color: 'var(--primary-cyan)', fontSize: '14px' }}>Detalles</h3>
                {!isEditingAppDetails ? (
                  <button onClick={(e) => { 
                    e.stopPropagation(); 
                    setIsEditingAppDetails(true); 
                    setEditAppDetails({
                      patient_name: optionsModalApp.patient_name || '',
                      patient_dni: optionsModalApp.patient_dni || optionsModalApp.study_details?.patient_dni || '',
                      study_type: optionsModalApp.study_type || '',
                      patient_phone: optionsModalApp.patient_phone || '',
                      patient_email: optionsModalApp.patient_email || '',
                      insurance_provider: optionsModalApp.study_details?.insurance_provider || ''
                    });
                  }} style={{ background: 'transparent', border: 'none', color: '#00e5ff', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}>Editar Datos</button>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={(e) => { e.stopPropagation(); setIsEditingAppDetails(false); }} style={{ background: 'transparent', border: '1px solid #555', color: 'white', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>Cancelar</button>
                    <button onClick={async (e) => { 
                      e.stopPropagation(); 
                      const study_details = { ...optionsModalApp.study_details, patient_dni: editAppDetails.patient_dni, insurance_provider: editAppDetails.insurance_provider };
                      const dataToUpdate = {
                        patient_name: editAppDetails.patient_name,
                        patient_dni: editAppDetails.patient_dni,
                        patient_phone: editAppDetails.patient_phone,
                        patient_email: editAppDetails.patient_email,
                        study_type: editAppDetails.study_type,
                        study_details: study_details
                      };
                      await updateAppointmentDetails(optionsModalApp.id, dataToUpdate);

                      // Sincronizar Ficha del Paciente y todos los turnos asociados
                      try {
                        const patientId = optionsModalApp.patient_id;
                        const origDni = optionsModalApp.patient_dni || optionsModalApp.study_details?.patient_dni;
                        const origName = optionsModalApp.patient_name;

                        let targetPatient = null;
                        if (patientId) {
                          const { data: pById } = await supabase.from('patients').select('*').eq('id', patientId).single();
                          targetPatient = pById;
                        }
                        if (!targetPatient && origDni) {
                          const { data: pByDni } = await supabase.from('patients').select('*').eq('dni', String(origDni).trim()).limit(1);
                          if (pByDni && pByDni.length > 0) targetPatient = pByDni[0];
                        }
                        if (!targetPatient && origName) {
                          const { data: pByName } = await supabase.from('patients').select('*').ilike('full_name', String(origName).trim()).limit(1);
                          if (pByName && pByName.length > 0) targetPatient = pByName[0];
                        }

                        const patientPayload = {
                          full_name: editAppDetails.patient_name,
                          dni: editAppDetails.patient_dni,
                          phone: editAppDetails.patient_phone,
                          email: editAppDetails.patient_email,
                          insurance_provider: editAppDetails.insurance_provider
                        };

                        let resolvedPatientId = patientId || targetPatient?.id || null;

                        if (targetPatient) {
                          await supabase.from('patients').update(patientPayload).eq('id', targetPatient.id);
                        }

                        const updatedDataWithId = {
                          ...dataToUpdate,
                          patient_id: resolvedPatientId
                        };

                        if (resolvedPatientId) {
                          await supabase.from('appointments').update(updatedDataWithId).eq('patient_id', resolvedPatientId);
                        }
                        if (origDni) {
                          await supabase.from('appointments').update(updatedDataWithId).eq('patient_dni', String(origDni).trim());
                        }
                        if (origName) {
                          await supabase.from('appointments').update(updatedDataWithId).ilike('patient_name', String(origName).trim());
                        }
                      } catch (syncErr) {
                        console.warn('Error al sincronizar datos del paciente:', syncErr);
                      }

                      setOptionsModalApp({ ...optionsModalApp, ...dataToUpdate });
                      setIsEditingAppDetails(false);
                    }} style={{ background: '#00e5ff', border: 'none', color: 'black', fontWeight: 'bold', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>Guardar</button>
                  </div>
                )}
              </div>

            <div className="expanded-details-grid" style={{ marginBottom: '1rem' }}>
              {!isEditingAppDetails ? (
                <>
                  <div className="detail-item"><span className="detail-label">Paciente:</span> <span className="detail-value highlight">{optionsModalApp.patient_name}</span></div>
                  <div className="detail-item"><span className="detail-label">Documento:</span> <span className="detail-value">{optionsModalApp.patient_dni || optionsModalApp.study_details?.patient_dni || 'N/A'}</span></div>
                  <div className="detail-item"><span className="detail-label">Estudio:</span> <span className="detail-value">{optionsModalApp.study_type || 'Consulta'}</span></div>
                  <div className="detail-item"><span className="detail-label">Fecha/Hora:</span> <span className="detail-value">{optionsModalApp.appointment_date} {optionsModalApp.appointment_time?.substring(0,5)}</span></div>
                  <div className="detail-item"><span className="detail-label">Teléfono:</span> <span className="detail-value">{optionsModalApp.patient_phone || 'N/A'}</span></div>
                  <div className="detail-item"><span className="detail-label">Email:</span> <span className="detail-value">{optionsModalApp.patient_email || 'N/A'}</span></div>
                  <div className="detail-item"><span className="detail-label">Cobertura:</span> <span className="detail-value">{optionsModalApp.study_details?.insurance_provider || 'PARTICULAR'}</span></div>
                </>
              ) : (
                <>
                  <div className="detail-item" style={{ display: 'flex', flexDirection: 'column' }}><span className="detail-label">Paciente:</span> <input type="text" value={editAppDetails.patient_name} onChange={(e) => setEditAppDetails({...editAppDetails, patient_name: e.target.value})} style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid #444', color: 'white', padding: '4px', borderRadius: '4px', marginTop: '4px' }} /></div>
                  <div className="detail-item" style={{ display: 'flex', flexDirection: 'column' }}><span className="detail-label">Documento:</span> <input type="text" value={editAppDetails.patient_dni} onChange={(e) => setEditAppDetails({...editAppDetails, patient_dni: e.target.value})} style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid #444', color: 'white', padding: '4px', borderRadius: '4px', marginTop: '4px' }} /></div>
                  <div className="detail-item" style={{ display: 'flex', flexDirection: 'column' }}><span className="detail-label">Estudio:</span> <input type="text" value={editAppDetails.study_type} onChange={(e) => setEditAppDetails({...editAppDetails, study_type: e.target.value})} style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid #444', color: 'white', padding: '4px', borderRadius: '4px', marginTop: '4px' }} /></div>
                  <div className="detail-item" style={{ display: 'flex', flexDirection: 'column' }}><span className="detail-label">Teléfono:</span> <input type="text" value={editAppDetails.patient_phone} onChange={(e) => setEditAppDetails({...editAppDetails, patient_phone: e.target.value})} style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid #444', color: 'white', padding: '4px', borderRadius: '4px', marginTop: '4px' }} /></div>
                  <div className="detail-item" style={{ display: 'flex', flexDirection: 'column' }}><span className="detail-label">Email:</span> <input type="text" value={editAppDetails.patient_email} onChange={(e) => setEditAppDetails({...editAppDetails, patient_email: e.target.value})} style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid #444', color: 'white', padding: '4px', borderRadius: '4px', marginTop: '4px' }} /></div>
                  <div className="detail-item" style={{ display: 'flex', flexDirection: 'column' }}><span className="detail-label">Cobertura:</span> <input type="text" value={editAppDetails.insurance_provider} onChange={(e) => setEditAppDetails({...editAppDetails, insurance_provider: e.target.value})} style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid #444', color: 'white', padding: '4px', borderRadius: '4px', marginTop: '4px' }} /></div>
                </>
              )}

              <div className="detail-item full-width notes-item" style={{ marginTop: '0.5rem', gridColumn: '1 / -1' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span className="detail-label">Observaciones Internas:</span>
                  {editingNotesId !== optionsModalApp.id && (
                    <button onClick={(e) => { e.stopPropagation(); setEditingNotesId(optionsModalApp.id); setEditNotesText(optionsModalApp.admin_notes || ''); }} style={{ background: 'transparent', border: 'none', color: '#00e5ff', cursor: 'pointer', fontSize: '11px', padding: '2px', textDecoration: 'underline' }}>Editar</button>
                  )}
                </div>
                {editingNotesId === optionsModalApp.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                    <textarea 
                      value={editNotesText} 
                      onChange={(e) => setEditNotesText(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: '100%', minHeight: '60px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', borderRadius: '4px', padding: '6px', fontFamily: 'inherit' }}
                      placeholder="Ingrese observaciones del turno..."
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                      <button onClick={(e) => { e.stopPropagation(); setEditingNotesId(null); }} style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #555', background: 'transparent', color: 'white', cursor: 'pointer', fontSize: '11px' }}>Cancelar</button>
                      <button onClick={async (e) => { 
                        e.stopPropagation(); 
                        await updateAppointmentNotes(optionsModalApp.id, editNotesText); 
                        setOptionsModalApp({...optionsModalApp, admin_notes: editNotesText}); 
                        setEditingNotesId(null); 
                      }} style={{ padding: '4px 8px', borderRadius: '4px', border: 'none', background: '#00e5ff', color: 'black', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px' }}>Guardar</button>
                    </div>
                  </div>
                ) : (
                  <p className="detail-notes">{optionsModalApp.admin_notes || <span style={{opacity: 0.5}}>- Sin observaciones -</span>}</p>
                )}
              </div>

              <div className="detail-item full-width files-item" style={{ marginTop: '0.5rem', gridColumn: '1 / -1' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span className="detail-label">Archivos Adjuntos (Órdenes, etc):</span>
                  <label style={{ background: 'transparent', border: 'none', color: '#00e5ff', cursor: 'pointer', fontSize: '11px', padding: '2px', textDecoration: 'underline' }}>
                    Subir Archivo
                    <input type="file" style={{ display: 'none' }} onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      try {
                        addNotification({ title: 'Subiendo...', message: 'Subiendo archivo...', type: 'info' });
                        const fileName = Date.now() + '_' + file.name.replace(/\s+/g, '_');
                        const { error: uploadError } = await supabase.storage.from('patient-docs').upload(fileName, file);
                        if (uploadError) throw uploadError;
                        const { data: { publicUrl } } = supabase.storage.from('patient-docs').getPublicUrl(fileName);
                        
                        const newFile = { name: file.name, url: publicUrl, study_name: 'Adicional' };
                        const currentFiles = optionsModalApp.study_details?.attached_files || [];
                        const study_details = { ...optionsModalApp.study_details, attached_files: [...currentFiles, newFile] };
                        
                        await updateAppointmentDetails(optionsModalApp.id, { study_details });
                        setOptionsModalApp({ ...optionsModalApp, study_details });
                        addNotification({ title: 'Éxito', message: 'Archivo subido', type: 'success' });
                      } catch(err) {
                        console.error(err);
                        addNotification({ title: 'Error', message: 'No se pudo subir el archivo', type: 'error' });
                      }
                    }} />
                  </label>
                </div>
                {optionsModalApp.study_details?.attached_files?.length > 0 ? (
                  <div className="attached-files-list" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {optionsModalApp.study_details.attached_files.map((file, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,229,255,0.05)', padding: '4px 8px', borderRadius: '4px', border: '1px solid rgba(0,229,255,0.1)' }}>
                        <a href={file.url} target="_blank" rel="noopener noreferrer" className="attached-file-link" onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#00e5ff', fontSize: '12px', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          <FileText size={12} style={{ flexShrink: 0 }} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                        </a>
                        <button
                          type="button"
                          title="Eliminar este archivo adjunto"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm('¿Está seguro de que desea eliminar este archivo adjunto?')) return;
                            try {
                              const currentFiles = optionsModalApp.study_details?.attached_files || [];
                              const updatedFiles = currentFiles.filter((_, i) => i !== idx);
                              const study_details = { ...optionsModalApp.study_details, attached_files: updatedFiles };
                              await updateAppointmentDetails(optionsModalApp.id, { study_details });
                              setOptionsModalApp({ ...optionsModalApp, study_details });
                              addNotification({ title: 'Éxito', message: 'Archivo eliminado', type: 'success' });
                            } catch(err) {
                              console.error(err);
                              addNotification({ title: 'Error', message: 'No se pudo eliminar el archivo', type: 'error' });
                            }
                          }}
                          style={{ background: 'transparent', border: 'none', color: '#ff5252', cursor: 'pointer', padding: '2px 4px', display: 'flex', alignItems: 'center' }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span style={{opacity: 0.5, fontSize: '12px'}}>- Sin archivos -</span>
                )}
              </div>
            </div>
            <div className="app-actions-container" style={{ margin: 0, justifyContent: 'center', width: '100%', flexWrap: 'wrap' }}>
              <StatusDropdown value={optionsModalApp.status || 'pendiente'} onChange={(newStatus) => { updateAppointmentStatus(optionsModalApp.id, newStatus); setOptionsModalApp({...optionsModalApp, status: newStatus}); }} />
              <div className="reports-actions-group" style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn-report-action chat" onClick={() => { if(optionsModalApp.patient_email) { window.location.href = `mailto:${optionsModalApp.patient_email}?subject=Consulta Turno - GDT`; } else { alert('El paciente no tiene email configurado.'); } }} title="Enviar correo" aria-label="Enviar correo"><Mail size={14} /></button>
                <button className="btn-report-action" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', borderColor: 'rgba(245, 158, 11, 0.2)' }} onClick={() => { setClipboardApp(optionsModalApp); setOptionsModalApp(null); }} title="Copiar a memoria para Mover" aria-label="Copiar a memoria para Mover"><RefreshCw size={14} /></button>
                <button className="btn-report-action email" onClick={() => { setEmailModalData(optionsModalApp); setOptionsModalApp(null); }} title="Enviar Expediente" aria-label="Enviar Expediente"><FileText size={14} /></button>
                <button className="btn-report-action" style={{ background: 'rgba(37, 211, 102, 0.1)', color: '#25D366', borderColor: 'rgba(37, 211, 102, 0.2)' }} onClick={() => window.open(`https://wa.me/${optionsModalApp.patient_phone?.replace(/\D/g, '')}`, '_blank')} title="WhatsApp" aria-label="WhatsApp"><MessageSquare size={14} /></button>
                <button className="btn-report-action" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', borderColor: 'rgba(59, 130, 246, 0.2)' }} onClick={(e) => { e.stopPropagation(); updateAppointmentStatus(optionsModalApp.id, 'en-llamado'); setOptionsModalApp({...optionsModalApp, status: 'en-llamado'}); }} title="Llamar al TV" aria-label="Llamar al TV"><Volume2 size={14} /></button>
                <button className="btn-report-action docs" onClick={(e) => { 
                  e.stopPropagation();
                  setOptionsModalApp(null);
                  if (optionsModalApp.patient_id) {
                    navigate(`/patients/${optionsModalApp.patient_id}`); 
                  } else {
                    sessionStorage.setItem('pending_create_patient', JSON.stringify({
                          full_name: optionsModalApp.patient_name || '',
                          phone: optionsModalApp.patient_phone || '',
                          email: optionsModalApp.patient_email || '',
                          dni: optionsModalApp.study_details?.patient_dni || ''
                    }));
                    navigate('/patients');
                  }
                }} title={optionsModalApp.patient_id ? "Carpeta Médica" : "Crear Ficha de Paciente"} aria-label={optionsModalApp.patient_id ? "Carpeta Médica" : "Crear Ficha de Paciente"}>
                  {optionsModalApp.patient_id ? <FileText size={14} /> : <UserPlus size={14} />}
                </button>
                <button className="btn-report-action delete" onClick={() => { deleteAppointment(optionsModalApp.id); setOptionsModalApp(null); }} title="Eliminar"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        )}
      </Modal>


      {isInboxOpen && (
        <Modal isOpen={true} title="Turnos desde el Portal (Sin Horario)" onClose={() => setIsInboxOpen(false)}>
          <div style={{ padding: '1rem', maxHeight: '60vh', overflowY: 'auto' }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
              Estos turnos fueron solicitados a través del portal web sin un horario específico. 
              Por favor contáctelos para acordar un horario, y luego presione "ASIGNAR HORARIO" para ubicarlos en la grilla.
            </p>
            {inboxLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner /></div>
            ) : inboxApps.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No hay turnos pendientes del portal.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {inboxApps.map(app => (
                  <div key={app.id} style={{ background: 'var(--bg-lighter)', padding: '1rem', borderRadius: '12px', border: '1px solid var(--border-glass)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                      <h4 style={{ margin: 0, color: 'var(--text-main)' }}>{app.patient_name}</h4>
                      <span style={{ fontSize: '0.8rem', background: 'rgba(0,229,255,0.1)', color: 'var(--primary-cyan)', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>{app.study_type}</span>
                    </div>
                    <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      <strong>DNI:</strong> {app.patient_dni || '-'} | <strong>Tel:</strong> {app.patient_phone || '-'}
                    </p>
                    <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      <strong>Profesional sugerido:</strong> {
                        (!app.professional_name || ['CONSULTORIOS EXTERNOS', 'VER TODOS', 'VER LOS QUE ATIENDEN x DIA', 'VER x SELECCIÓN', 'AGREGAR OTRO PROFESIONAL', 'Sin Asignar', '-'].includes(app.professional_name))
                          ? <span style={{ opacity: 0.75, fontStyle: 'italic', color: 'var(--text-muted)' }}>Sin sugerencia</span>
                          : app.professional_name
                      }
                    </p>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {app.patient_phone && (
                        <a href={`https://wa.me/${app.patient_phone.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="btn-secondary" style={{ flex: 1, textAlign: 'center', textDecoration: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <MessageSquare size={14} style={{ marginRight: '5px' }} /> CONTACTAR
                        </a>
                      )}
                      <button className="btn-primary" style={{ flex: 1 }} onClick={() => {
                        setIsInboxOpen(false);
                        setModalData(app);
                        setIsModalOpen(true);
                      }}>ASIGNAR HORARIO</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Calendar;

